import React, { useEffect, useState, useCallback } from "react";
import api from "../../api/client";
import { useLanguage } from "../../contexts/LanguageContext";
import { Link, useLocation } from "react-router-dom";

// Tabs
import ProfileTab from "../../screens/profile/tabs/ProfileTab";
import AddressesTab from "../../screens/profile/tabs/AddressesTab";
import DevicesTab from "../../screens/profile/tabs/DevicesTab";
import MyOrders from "./MyOrders";
import TrackingTab from "../../screens/profile/tabs/TrackingTab";

// Existing
import MyInvoices from "./MyInvoices";
import ProfileWalletTab from "../../screens/profile/ProfileWalletTab";
import MyDownloads from "../../screens/account/MyDownloads";
import MyCourses from "../../pages/MyCourses";
import MyConsultations from "../../pages/MyConsultations";
import MyJobApplications from "../../pages/MyJobApplications";
import MyServiceOrders from "../../pages/MyServiceOrders";
import logger from "../../utils/logger.js";

// UI
import Breadcrumb from "../../components/ui/Breadcrumb/Breadcrumb";
import LoadingScreen from "../../components/ui/Loading/Loading";
import useBurgerMenu from "../../hooks/useBurgerMenu";
import {
  User,
  MapPin,
  Monitor,
  Wallet,
  Package,
  Download,
  Receipt,
  Truck,
  GraduationCap,
  Lightbulb,
  Wrench,
  Briefcase,
  AlertTriangle,
  Store,
  ArrowUpRight,
  LayoutGrid,
  X,
} from "lucide-react";

import "./Account.css";

export default function Account() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const location = useLocation();
  const hasBurgerMenu = useBurgerMenu();

  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [form, setForm] = useState({
    name: "",
    avatar_url: "",
    phone: "",
    address: "",
    street_address: "",
    neighborhood: "",
    city: "",
    country_name: "",
    country_code: "",
    password: "",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [retryKey, setRetryKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  const showProvider = true;

  const labels = {
    en: {
      myAccount: "My Account",
      profile: "Profile",
      addresses: "Addresses",
      devices: "Devices",
      wallet: "Wallet",
      orders: "Orders",
      downloads: "Downloads",
      invoices: "Invoices",
      tracking: "Tracking",
      courses: "Courses",
      consultations: "Consultations",
      services: "Services",
      jobs: "Jobs",
      provider: "Provider Dashboard",
      menu: "Menu",
      allServices: "All Services",
      loading: "Loading...",
      loadError: "Error loading account data",
      retry: "Try Again",
    },
    ar: {
      myAccount: "حسابي",
      profile: "الملف الشخصي",
      addresses: "العناوين",
      devices: "الأجهزة",
      wallet: "المحفظة",
      orders: "طلباتي",
      downloads: "التحميلات",
      invoices: "الفواتير",
      tracking: "التتبع",
      courses: "الكورسات",
      consultations: "الاستشارات",
      services: "الخدمات",
      jobs: "الوظائف",
      provider: "لوحة المزود",
      menu: "القائمة",
      allServices: "كل الخدمات",
      loading: "جارِ التحميل...",
      loadError: "حدث خطأ أثناء تحميل البيانات",
      retry: "إعادة المحاولة",
    },
  }[lang];

  const allNavItems = [
    { key: "profile", Icon: User, label: labels.profile },
    { key: "orders", Icon: Package, label: labels.orders },
    { key: "wallet", Icon: Wallet, label: labels.wallet },
    { key: "addresses", Icon: MapPin, label: labels.addresses },
    { key: "devices", Icon: Monitor, label: labels.devices },
    { key: "invoices", Icon: Receipt, label: labels.invoices },
    { key: "tracking", Icon: Truck, label: labels.tracking },
    { key: "downloads", Icon: Download, label: labels.downloads },
    { key: "courses", Icon: GraduationCap, label: labels.courses },
    { key: "consultations", Icon: Lightbulb, label: labels.consultations },
    { key: "services", Icon: Wrench, label: labels.services },
    { key: "jobs", Icon: Briefcase, label: labels.jobs },
  ];

  const topNavItems = [
    allNavItems[0],
    allNavItems[1],
    allNavItems[2],
  ];

  const loadAccountData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const profile = await api.apiGet("me");
      const userData = profile?.data || profile?.user;
      if (userData) {
        setUser(userData);
        setForm({
          name: userData.name || "",
          avatar_url: userData.avatar_url || "",
          phone: userData.phone || "",
          address: userData.address || "",
          street_address: userData.street_address || "",
          neighborhood: userData.neighborhood || "",
          city: userData.city || "",
          country_name: userData.country_name || "",
          country_code: userData.country_code || "",
          password: "",
        });
      }

      const ordersRes = await api.apiGet("orders");
      setOrders(Array.isArray(ordersRes?.orders) ? ordersRes.orders : []);

      const invRes = await api.apiGet("invoices");
      setInvoices(Array.isArray(invRes?.invoices) ? invRes.invoices : []);
    } catch (e) {
      logger.error(e);
      setError(labels.loadError);
    } finally {
      setLoading(false);
    }
  }, [labels.loadError]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData, retryKey]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const allowed = allNavItems.map((n) => n.key);
    if (tab && allowed.includes(tab)) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";

      const navbar = document.querySelector(".mg-navbar") || document.querySelector("nav");
      const topBar = document.querySelector(".account-top-bar");
      const navH = navbar ? navbar.getBoundingClientRect().height : 52;
      const barH = topBar ? topBar.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty(
        "--navbar-total-height",
        `${Math.round(navH + barH)}px`
      );
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleRetry = useCallback(() => {
    setRetryKey((k) => k + 1);
  }, []);

  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbItems = [{ label: labels.myAccount }];
  const shouldShowBreadcrumb = hasBurgerMenu;

  if (loading) {
    return (
      <div className="account-page" dir={isAr ? "rtl" : "ltr"}>
        {shouldShowBreadcrumb && <Breadcrumb items={breadcrumbItems} />}
        <header className="account-header">
          <div className="account-header-content">
            <div className="account-title-wrapper">
              <div className="account-header-icon">
                <User size={24} strokeWidth={2} />
              </div>
              <h1 className="account-header-title">{labels.myAccount}</h1>
            </div>
          </div>
        </header>
        <div className="account-container">
          <LoadingScreen lang={lang} minHeight="60vh" />
        </div>
      </div>
    );
  }

  return (
    <div className="account-page" dir={isAr ? "rtl" : "ltr"}>
      {shouldShowBreadcrumb && <Breadcrumb items={breadcrumbItems} />}

      <header className="account-header">
        <div className="account-header-content">
          <div className="account-title-wrapper">
            <div className="account-header-icon">
              <User size={24} strokeWidth={2} />
            </div>
            <h1 className="account-header-title">{labels.myAccount}</h1>
          </div>

          {showProvider && (
            <Link
              to="/provider-dashboard"
              className="account-provider-header-btn"
              aria-label={labels.provider}
            >
              <span className="account-provider-header-inner">
                <Store size={16} strokeWidth={2} />
                <span>{labels.provider}</span>
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </span>
            </Link>
          )}
        </div>
      </header>

      {hasBurgerMenu && (
        <div className="account-top-bar">
          <div className="account-top-bar-inner">
            {topNavItems.map((item) => {
              const Icon = item.Icon;
              const isActive = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  className={`account-top-item ${isActive ? "active" : ""}`}
                  onClick={() => handleTabChange(item.key)}
                  type="button"
                >
                  <Icon size={18} strokeWidth={2.5} />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <button
              className={`account-top-item ${menuOpen ? "active" : ""}`}
              onClick={() => setMenuOpen(true)}
              type="button"
            >
              <LayoutGrid size={18} strokeWidth={2.5} />
              <span>{labels.menu}</span>
            </button>
          </div>
        </div>
      )}

      <div className="account-layout">
        {/* Desktop Sidebar */}
        <aside className="account-sidebar" role="navigation" aria-label={labels.myAccount}>
          <nav className="account-nav">
            {allNavItems.map((item) => {
              const Icon = item.Icon;
              const isActive = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  className={`account-nav-item ${isActive ? "active" : ""}`}
                  onClick={() => handleTabChange(item.key)}
                  type="button"
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className="account-nav-icon">
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  <span className="account-nav-label">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="account-main">
          {error ? (
            <div className="account-error">
              <div className="account-error-icon">
                <AlertTriangle size={40} strokeWidth={1.5} />
              </div>
              <p className="account-error-message">{error}</p>
              <button className="account-btn-primary" onClick={handleRetry} type="button">
                {labels.retry}
              </button>
            </div>
          ) : (
            <div className="account-content-card">
              {activeTab === "profile" && (
                <ProfileTab
                  user={user}
                  form={form}
                  setForm={setForm}
                  isAr={isAr}
                  labels={labels}
                  onOpenAddresses={() => handleTabChange("addresses")}
                />
              )}

              {activeTab === "addresses" && <AddressesTab isAr={isAr} />}
              {activeTab === "devices" && <DevicesTab isAr={isAr} />}
              {activeTab === "orders" && (
  <MyOrders orders={orders} isAr={isAr} embedded />
)}
              {activeTab === "invoices" && <MyInvoices isAr={isAr} lang={lang} />}
              {activeTab === "downloads" && <MyDownloads />}
              {activeTab === "courses" && <MyCourses embedded />}
              {activeTab === "consultations" && <MyConsultations embedded />}
              {activeTab === "jobs" && <MyJobApplications embedded />}
              {activeTab === "services" && <MyServiceOrders embedded />}
              {activeTab === "wallet" && <ProfileWalletTab isAr={isAr} />}
              {activeTab === "tracking" && (
                <TrackingTab orders={orders} isAr={isAr} labels={labels} />
              )}
            </div>
          )}
        </main>
      </div>

      {/* Full-Screen Menu Modal */}
      {hasBurgerMenu && menuOpen && (
        <>
          <div
            className="account-menu-backdrop"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="account-menu-modal" role="dialog" aria-modal="true">
            <div className="account-menu-header">
              <h2>{labels.allServices}</h2>
              <button
                className="account-menu-close"
                onClick={() => setMenuOpen(false)}
                type="button"
                aria-label="Close"
              >
                <X size={24} strokeWidth={2} />
              </button>
            </div>
            <div className="account-menu-grid">
              {allNavItems.map((item) => {
                const Icon = item.Icon;
                const isActive = activeTab === item.key;
                return (
                  <button
                    key={item.key}
                    className={`account-menu-card ${isActive ? "active" : ""}`}
                    onClick={() => handleTabChange(item.key)}
                    type="button"
                  >
                    <span className="account-menu-card-icon">
                      <Icon size={28} strokeWidth={2} />
                    </span>
                    <span className="account-menu-card-label">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}